import { useCallback, useState } from "react";
import type { Workflow } from "@/types/aiToolkit";
import { useWorkflowRun } from "@/hooks/useWorkflowRun";

export function useCommunicationWorkflow(integrationSource: string) {
  const [selectedWorkflowId, setSelectedWorkflowId] = useState<string | null>(null);
  const [selectedWorkflow, setSelectedWorkflow] = useState<Workflow | null>(null);
  const [inspectorOpen, setInspectorOpen] = useState(false);
  const { activeRunId, isRunning, progressLabel, startWorkflow } = useWorkflowRun();

  const onWorkflowChange = useCallback((id: string | null, wf: Workflow | null) => {
    setSelectedWorkflowId(id);
    setSelectedWorkflow(wf);
  }, []);

  const runWorkflow = useCallback(
    async (
      buildPayload:
        | (() => Promise<Record<string, unknown>> | Record<string, unknown>)
        | Record<string, unknown>
    ) => {
      if (!selectedWorkflow) return null;
      const payload =
        typeof buildPayload === "function" ? await buildPayload() : buildPayload;
      const run = await startWorkflow(selectedWorkflow, {
        ...payload,
        source: integrationSource,
        executionMode: "manual",
      });
      if (run) setInspectorOpen(true);
      return run;
    },
    [integrationSource, selectedWorkflow, startWorkflow]
  );

  const batchProgress =
    isRunning && progressLabel
      ? { completed: 0, total: 1, currentLabel: progressLabel }
      : undefined;

  return {
    integrationSource,
    selectedWorkflowId,
    selectedWorkflow,
    onWorkflowChange,
    runWorkflow,
    isWorkflowRunning: isRunning,
    workflowBatchProgress: batchProgress,
    activeRunId,
    inspectorOpen,
    setInspectorOpen,
  };
}
